import { ReactNode } from "react";

import { clsx } from "clsx";
import { useParams, To } from "react-router-dom";

import { BadgeStatus } from "../../atoms";
import { RVPaddedPage, RVNestedPageHeader, RVRow, RVColumn } from "../../layouts";
import { RVStatusDisplay } from "../../molecules";

import styles from "./RVFormPageLayout.module.scss";

/**
 * Props for the RVFormPageLayout component.
 */
export type RVFormPageLayoutProps = {
    /** The main title of the page (can be a string or React element) */
    title: string | ReactNode;
    /** Optional subtitle displayed below the title */
    subtitle?: string | ReactNode;
    /** Entity ID to display in the status badge */
    id: string | number | undefined;
    /** Status state with label and badge status type */
    state?: { label: string; status: BadgeStatus };
    /** The main content of the form page */
    children: ReactNode;
    /** Optional footer content (typically action buttons) */
    footer?: ReactNode;
    /** Whether to use wide page layout for full-width content */
    wide?: boolean;
    /** Whether to display the status badge */
    showStatus?: boolean;
    /** Additional CSS class name for the container */
    containerClassName?: string;
    /** Optional prefix element (e.g., icon) before the title */
    prefix?: JSX.Element;
    /** Additional action elements to display in the title row */
    titleRowActions?: ReactNode;
    /** URL path for the back link */
    backTo?: To;
    /** Text label for the back link */
    backText?: string;
    /**
     * Visual style for the sticky footer bar.
     * `divider` matches settings form footers: flat bar, top border, no card shadow.
     */
    footerVariant?: "default" | "divider";
    /**
     * When true, the page padding is applied only to the header, and the content
     * area bleeds edge-to-edge. Useful for full-bleed content such as code editors.
     * @default false
     */
    flushContent?: boolean;
};

/**
 * A standardized layout component for form pages with header, content area, and optional footer.
 *
 * Features:
 * - Displays page title, subtitle, and status badge
 * - Supports version display from route params
 * - Optional back navigation
 * - Custom title row actions
 * - Sticky footer for action buttons
 * - Wide layout option for full-width content
 *
 * @example
 * ```tsx
 * <RVFormPageLayout
 *   title="Campaign Editor"
 *   subtitle="Configure your campaign"
 *   id={123}
 *   state={{ label: "Active", status: BADGE_STATUS.ACTIVE }}
 *   footer={<Button>Save</Button>}
 * >
 *   <YourFormContent />
 * </RVFormPageLayout>
 * ```
 */
export function RVFormPageLayout({
    title,
    subtitle,
    id,
    state,
    children,
    footer,
    wide = false,
    showStatus = true,
    containerClassName,
    prefix,
    titleRowActions,
    backTo,
    backText,
    footerVariant = "default",
    flushContent = false,
}: RVFormPageLayoutProps): JSX.Element {
    const { version } = useParams<{ version: string }>();

    const statusBadge = showStatus ? (
        <RVStatusDisplay
            data-testid="form-container-status"
            id={id}
            status={state?.status}
            content={state?.label}
            version={version}
        />
    ) : null;

    return (
        <RVColumn className={clsx(styles.container, containerClassName)} alignItems="stretch" justifyContent="stretch">
            <RVPaddedPage noPadding={flushContent} className={clsx({ [styles.flushPaddedPage]: flushContent })}>
                <div className={clsx({ [styles.headerPadded]: flushContent })}>
                    <RVNestedPageHeader
                        title={title}
                        subtitle={subtitle}
                        prefix={prefix}
                        backTo={backTo}
                        backText={backText}
                        titleRowActions={
                            <>
                                {titleRowActions}
                                {statusBadge}
                            </>
                        }
                        titleActionAlignment={subtitle ? "bottom" : "top"}
                    />
                </div>

                <div
                    className={clsx(styles.content, {
                        [styles.widePage]: wide,
                        [styles.contentFlush]: flushContent,
                    })}
                >
                    <div className={styles.fullWidth}>{children}</div>
                </div>
            </RVPaddedPage>
            {footer && (
                <RVRow
                    className={clsx(styles.footer, {
                        [styles.footerDivider]: footerVariant === "divider",
                    })}
                    justifyContent="space-between"
                    alignItems="center"
                >
                    {footer}
                </RVRow>
            )}
        </RVColumn>
    );
}
